import React, { useState } from 'react';
import {
    EuiCard,
    EuiFlexGroup,
    EuiFlexItem,
    EuiButton,
    EuiSpacer,
    EuiBasicTable,
    EuiText,
    EuiRadioGroup,
    EuiModal,
    EuiModalHeader,
    EuiModalHeaderTitle,
    EuiModalBody,
    EuiModalFooter,
    EuiButtonEmpty,
    EuiFormRow,
    EuiFieldNumber,
    EuiCheckboxGroup,
    EuiCallOut,
    EuiSelect,
} from '@elastic/eui';
import { useFieldArray } from 'react-hook-form';

const RewardsCatalog = ({ control, errors, watch, setValue, getValues }) => {
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [editingIndex, setEditingIndex] = useState(null);
    const [selectedItem, setSelectedItem] = useState(null);
    const [pointsRequired, setPointsRequired] = useState('');
    const [selectedStores, setSelectedStores] = useState([]);

    const {
        fields: catalogFields,
        append: appendItem,
        remove: removeItem,
        update: updateItem,
    } = useFieldArray({
        control,
        name: 'rewards_catalog',
    });

    const outOfStockOptions = [
        { id: 'hide', label: 'Temporarily hide from catalog' },
        { id: 'show_out_of_stock', label: 'Show as "Out of Stock"' },
    ];

    // Mock menu items - in real implementation, this would come from API
    const menuItems = [
        { id: 1, name: 'French Fries', category: 'Sides', price: 120 },
        { id: 2, name: 'Soft Drink', category: 'Beverages', price: 60 },
        { id: 3, name: 'Chocolate Cake', category: 'Desserts', price: 150 },
        { id: 4, name: 'Pizza Slice', category: 'Mains', price: 200 },
        { id: 5, name: 'Ice Cream', category: 'Desserts', price: 80 },
        { id: 6, name: 'Burger', category: 'Mains', price: 180 },
        { id: 7, name: 'Salad', category: 'Sides', price: 100 },
        { id: 8, name: 'Coffee', category: 'Beverages', price: 70 },
    ];

    // Mock stores - in real implementation, this would come from restaurant data
    const storeOptions = [
        { id: 'store1', label: 'Downtown Outlet' },
        { id: 'store2', label: 'Mall Location' },
        { id: 'store3', label: 'Airport Terminal' },
    ];

    const columns = [
        {
            field: 'name',
            name: 'Item Name',
            sortable: true,
        },
        {
            field: 'category',
            name: 'Category',
            sortable: true,
        },
        {
            field: 'points_required',
            name: 'Points',
            render: (points) => `${points} pts`,
            sortable: true,
        },
        {
            name: 'Actions',
            actions: [
                {
                    name: 'Edit',
                    description: 'Edit reward item',
                    type: 'icon',
                    icon: 'pencil',
                    onClick: (item) => handleEditItem(item),
                },
                {
                    name: 'Delete',
                    description: 'Delete reward item',
                    type: 'icon',
                    icon: 'trash',
                    color: 'danger',
                    onClick: (item) => handleDeleteItem(item),
                },
            ],
        },
    ];

    const handleAddItem = () => {
        setEditingIndex(null);
        setSelectedItem(null);
        setPointsRequired('');
        setSelectedStores([]);
        setIsModalVisible(true);
    };

    const handleEditItem = (item) => {
        const index = catalogFields.findIndex((field) => field.id === item.id);
        setEditingIndex(index);
        setSelectedItem(item);
        setPointsRequired(item.points_required);
        setSelectedStores(item.available_stores || []);
        setIsModalVisible(true);
    };

    const handleDeleteItem = (item) => {
        const index = catalogFields.findIndex((field) => field.id === item.id);
        if (index >= 0) {
            removeItem(index);
        }
    };

    const handleSaveItem = () => {
        if (!selectedItem || !pointsRequired) return;

        const rewardItem = {
            ...selectedItem,
            points_required: parseInt(pointsRequired),
            available_stores: selectedStores.length > 0 ? selectedStores : ['all'],
        };

        if (editingIndex !== null) {
            updateItem(editingIndex, rewardItem);
        } else {
            appendItem(rewardItem);
        }

        setIsModalVisible(false);
    };

    const closeModal = () => {
        setIsModalVisible(false);
        setSelectedItem(null);
        setPointsRequired('');
        setSelectedStores([]);
    };

    return (
        <>
            <EuiCard
                title='Rewards Catalog'
                description='Select menu items that customers can redeem with points'
                paddingSize='l'
            >
                <EuiFlexGroup direction='column' gutterSize='l'>
                    {/* Out of Stock Behavior Section */}
                    <EuiFlexItem>
                        <EuiFlexGroup alignItems='center' gutterSize='m'>
                            <EuiFlexItem grow={false}>
                                <EuiText size='s'>
                                    <strong>Out of Stock Behavior:</strong>
                                </EuiText>
                            </EuiFlexItem>
                            <EuiFlexItem grow={3}>
                                <EuiRadioGroup
                                    options={outOfStockOptions}
                                    idSelected={watch('out_of_stock_behavior')}
                                    onChange={(id) => setValue('out_of_stock_behavior', id, { shouldDirty: true })}
                                    name='out_of_stock_behavior_radio'
                                    compressed
                                />
                            </EuiFlexItem>
                            <EuiFlexItem grow={false}>
                                <EuiButton fill iconType='plusInCircle' onClick={handleAddItem} size='s'>
                                    Add Item
                                </EuiButton>
                            </EuiFlexItem>
                        </EuiFlexGroup>
                    </EuiFlexItem>

                    {/* Catalog Table Section */}
                    <EuiFlexItem>
                        {catalogFields.length === 0 ? (
                            <EuiCallOut title='No rewards added yet' iconType='info' color='primary'>
                                <p>Click "Add Item" to start building your rewards catalog.</p>
                            </EuiCallOut>
                        ) : (
                            <EuiBasicTable
                                items={catalogFields}
                                columns={columns}
                                hasActions={true}
                                responsive={true}
                                tableLayout='auto'
                            />
                        )}
                    </EuiFlexItem>
                </EuiFlexGroup>
            </EuiCard>

            {isModalVisible && (
                <EuiModal onClose={closeModal}>
                    <EuiModalHeader>
                        <EuiModalHeaderTitle>{editingIndex !== null ? 'Edit' : 'Add'} Reward Item</EuiModalHeaderTitle>
                    </EuiModalHeader>

                    <EuiModalBody>
                        <EuiFormRow label='Select Menu Item'>
                            <EuiSelect
                                value={selectedItem?.id || ''}
                                onChange={(e) => {
                                    const itemId = parseInt(e.target.value);
                                    const item = menuItems.find((i) => i.id === itemId);
                                    setSelectedItem(item);
                                }}
                                disabled={editingIndex !== null}
                            >
                                <option value=''>Select a menu item...</option>
                                {menuItems.map((item) => (
                                    <option key={item.id} value={item.id}>
                                        {item.name} - {item.category} (₹{item.price})
                                    </option>
                                ))}
                            </EuiSelect>
                        </EuiFormRow>

                        {selectedItem && (
                            <>
                                <EuiSpacer size='m' />

                                <EuiText size='s'>
                                    <p>
                                        <strong>Selected:</strong> {selectedItem.name}
                                    </p>
                                    <p>
                                        <strong>Category:</strong> {selectedItem.category}
                                    </p>
                                    <p>
                                        <strong>Regular Price:</strong> ₹{selectedItem.price}
                                    </p>
                                </EuiText>

                                <EuiSpacer size='m' />

                                <EuiFormRow label='Points Required'>
                                    <EuiFieldNumber
                                        value={pointsRequired}
                                        onChange={(e) => setPointsRequired(e.target.value)}
                                        min={10}
                                        max={10000}
                                        append='points'
                                        compressed
                                    />
                                </EuiFormRow>

                                <EuiSpacer size='m' />

                                <EuiFormRow label='Store Availability'>
                                    <EuiCheckboxGroup
                                        options={[{ id: 'all', label: 'All Stores' }, ...storeOptions]}
                                        idToSelectedMap={
                                            selectedStores.includes('all') || selectedStores.length === 0
                                                ? { all: true }
                                                : selectedStores.reduce((acc, id) => ({ ...acc, [id]: true }), {})
                                        }
                                        onChange={(id) => {
                                            if (id === 'all') {
                                                setSelectedStores(['all']);
                                            } else {
                                                const newStores = selectedStores.filter((s) => s !== 'all');
                                                if (selectedStores.includes(id)) {
                                                    setSelectedStores(newStores.filter((s) => s !== id));
                                                } else {
                                                    setSelectedStores([...newStores, id]);
                                                }
                                            }
                                        }}
                                    />
                                </EuiFormRow>
                            </>
                        )}
                    </EuiModalBody>

                    <EuiModalFooter>
                        <EuiButtonEmpty onClick={closeModal}>Cancel</EuiButtonEmpty>
                        <EuiButton onClick={handleSaveItem} fill disabled={!selectedItem || !pointsRequired}>
                            {editingIndex !== null ? 'Update' : 'Add'} Item
                        </EuiButton>
                    </EuiModalFooter>
                </EuiModal>
            )}
        </>
    );
};

export default RewardsCatalog;
